import axios from 'axios';
import { snakeToCamel, camelToSnake } from './utils';
import { realtimeService } from '../services/realtimeService';

// Production API URL - corrected to match actual deployed service
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://sabalsql-dbam-ji2csljmda-uk.a.run.app';

// Environment-based debug logging
const DEBUG_MODE = false; // Temporarily disable debug mode to reduce console spam

// Phase 4: memory-only Authorization header support for SOMEAC
let AUTH_TOKEN = '';
export function setAuthToken(token) {
  AUTH_TOKEN = token || '';
  realtimeService.authToken = AUTH_TOKEN;
}
export function clearAuthToken() {
  AUTH_TOKEN = '';
  realtimeService.authToken = '';
}

/**
 * Base request function for API calls
 * @param {string} method - HTTP method (get, post, put, delete)
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Data for POST/PUT requests
 * @param {Object} params - Query parameters
 * @param {Object} options - Request transformation options
 * @returns {Promise} - Promise with API response
 */
export async function makeRequest(method, endpoint, data = null, params = null, options = {}) {
  if (DEBUG_MODE) {
    console.log(`=== API REQUEST DEBUG ===`);
    console.log(`Method: ${method.toUpperCase()}`);
    console.log(`Endpoint: ${endpoint}`);
    console.log(`Original data:`, data);
    console.log(`Params:`, params);
  }
  
  try {
    // Transform data from camelCase to snake_case for API
    const transformedData = data
      ? (options.transformData === false ? data : camelToSnake(data))
      : null;
    if (DEBUG_MODE) {
      console.log(`Transformed data for API:`, transformedData);
    }
    
    // Build axios configuration following architecture standards
    const config = {
      method: method.toLowerCase(),
      url: `${API_BASE_URL}${endpoint}`,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(AUTH_TOKEN ? { 'Authorization': `Bearer ${AUTH_TOKEN}` } : {})
      },
      params: params,
      timeout: options.timeout || 10000,
      retry: 2, // Retry failed requests
      retryDelay: 1000 // Wait 1 second between retries
    };
    if (options.signal) config.signal = options.signal;

    // Only include data property when it's not null (critical for DELETE requests)
    if (transformedData !== null) {
      config.data = transformedData;
    }

    if (DEBUG_MODE) {
      console.log(`Axios config:`, config);
    }
    
    const response = await axios(config);
    
    if (DEBUG_MODE) {
      console.log(`Response status: ${response.status}`);
      console.log(`Response headers:`, response.headers);
      console.log(`Response data:`, response.data);
    }
    
    // Transform response from snake_case to camelCase for frontend
    const responseData = response.data ? (options.preserveResponseKeys ? response.data : snakeToCamel(response.data)) : null;
    
    if (DEBUG_MODE) {
      console.log(`Transformed response data:`, responseData);
    }

    const result = {
      success: true,
      status: response.status,
      data: responseData,
      message: responseData?.message || response.statusText
    };
    
    if (DEBUG_MODE) {
      console.log(`Final result:`, result);
      console.log(`=== END API REQUEST DEBUG ===`);
    }
    
    return result;
  } catch (error) {
    if (DEBUG_MODE) {
      console.error(`API request failed:`, error);
      console.error(`Error response:`, error.response);
      console.error(`Error message:`, error.message);
      console.log(`=== END API REQUEST DEBUG ===`);
    }
    
    const errorBody = error.response?.data ? snakeToCamel(error.response.data) : null;

    // Maintain consistent error response format (and preserve DBAM error_code/message when present)
    return {
      success: false,
      status: error.response?.status || 0,
      data: errorBody,
      message: errorBody?.message || error.message || 'Network error'
    };
  }
}

// Auth API Service
export const meService = {
  getMe: () => makeRequest('get', '/api/me'),
  openDemo: () => makeRequest('post', '/api/auth/demo-guest', {})
};

// Admin Users API Service (superuser-only)
export const adminUsersService = {
  /**
   * List users (server-side pagination)
   * @param {Object} params
   * @param {number} params.limit
   * @param {number} params.offset
   * @param {string} params.q
   * @param {string} params.roleName  - maps to DBAM role_name
   * @param {string} params.customerId - maps to DBAM customer_id
   * @param {boolean|string|number} params.isActive - maps to DBAM is_active (true/false/1/0)
   */
  listUsers: ({ limit = 100, offset = 0, q, roleName, customerId, isActive } = {}) => {
    const params = { limit, offset };
    if (q !== undefined && q !== null && String(q).trim() !== '') params.q = String(q).trim();
    // Use bracket notation to avoid eslint camelcase warnings for API param names.
    if (roleName) params['role_name'] = roleName;
    if (customerId) params['customer_id'] = customerId;
    if (isActive !== undefined && isActive !== null) params['is_active'] = isActive;
    return makeRequest('get', '/api/admin/users', null, params);
  },

  createUser: (payload) => makeRequest('post', '/api/admin/users', payload),
  updateUser: (userId, payload) => makeRequest('put', `/api/admin/users/${userId}`, payload),
  replaceUserRoles: (userId, roles) => makeRequest('put', `/api/admin/users/${userId}/roles`, { roles }),
  deleteUser: (userId) => makeRequest('delete', `/api/admin/users/${userId}`)
};


// Admin Devices API Service (superuser-only)
export const adminDevicesService = {
  listUserDevices: (userId) => makeRequest('get', `/api/admin/users/${userId}/devices`),
  setServicePanelCode: (deviceUuid, servicePanelCode) =>
    makeRequest('put', `/api/admin/devices/${deviceUuid}/service-panel-code`, { servicePanelCode }),
  updateDevice: (deviceUuid, payload) =>
    makeRequest('put', `/api/admin/devices/${deviceUuid}`, payload),
  resetBinding: (deviceUuid) =>
    makeRequest('delete', `/api/admin/devices/${deviceUuid}/binding`)
};

// Customer API Service
export const customerService = {
  /**
   * Get all customers with pagination
   * @param {number} limit - Number of results to return
   * @param {number} offset - Offset for pagination
   * @returns {Promise} - Promise with customers data
   */
  getCustomers: (limit = 100, offset = 0) => 
    makeRequest('get', '/api/customers', null, { limit, offset }),
  
  /**
   * Get customer by ID
   * @param {string} customerId - Customer ID
   * @returns {Promise} - Promise with customer data
   */
  getCustomerById: (customerId) => 
    makeRequest('get', `/api/customers/${customerId}`),
    
  /**
   * Create a new customer
   * @param {Object} customerData - Customer data object
   * @returns {Promise} - Promise with created customer data
   */
  createCustomer: (customerData) => 
    makeRequest('post', '/api/customers', customerData),
    
  /**
   * Update an existing customer
   * @param {string} customerId - Customer ID
   * @param {Object} customerData - Updated customer data
   * @returns {Promise} - Promise with updated customer data
   */
  updateCustomer: (customerId, customerData) => 
    makeRequest('put', `/api/customers/${customerId}`, customerData),
    
  /**
   * Delete a customer
   * @param {string} customerId - Customer ID to delete
   * @returns {Promise} - Promise with deletion result
   */
  deleteCustomer: (customerId) => 
    makeRequest('delete', `/api/customers/${customerId}`)
};

// Fleet API Service
export const fleetService = {
  /**
   * Get all fleets with pagination
   * @param {number} limit - Number of results to return
   * @param {number} offset - Offset for pagination
   * @returns {Promise} - Promise with fleets data
   */
  getFleets: (limit = 100, offset = 0) => 
    makeRequest('get', '/api/fleets', null, { limit, offset }),
  
  /**
   * Get fleets for a specific customer
   * @param {string} customerId - Customer ID
   * @param {number} limit - Number of results to return
   * @param {number} offset - Offset for pagination
   * @returns {Promise} - Promise with customer's fleets data
   */
  getFleetsByCustomerId: (customerId, limit = 100, offset = 0) => 
    makeRequest('get', `/api/customers/${customerId}/fleets`, null, { limit, offset }),
  
  /**
   * Get fleet by ID
   * @param {string} fleetId - Fleet ID
   * @returns {Promise} - Promise with fleet data
   */
  getFleetById: (fleetId) => 
    makeRequest('get', `/api/fleets/${fleetId}`),
    
  /**
   * Create a new fleet
   * @param {Object} fleetData - Fleet data object
   * @returns {Promise} - Promise with created fleet data
   */
  createFleet: (fleetData) => 
    makeRequest('post', '/api/fleets', fleetData),
    
  /**
   * Update an existing fleet
   * @param {string} fleetId - Fleet ID
   * @param {Object} fleetData - Updated fleet data
   * @returns {Promise} - Promise with updated fleet data
   */
  updateFleet: (fleetId, fleetData) => 
    makeRequest('put', `/api/fleets/${fleetId}`, fleetData),
    
  /**
   * Delete a fleet
   * @param {string} fleetId - Fleet ID to delete
   * @returns {Promise} - Promise with deletion result
   */
  deleteFleet: (fleetId) => 
    makeRequest('delete', `/api/fleets/${fleetId}`)
};

// Canopy API Service
export const canopyService = {
  /**
   * Get all canopies with pagination
   * @param {number} limit - Number of results to return
   * @param {number} offset - Offset for pagination
   * @returns {Promise} - Promise with canopies data
   */
  getCanopies: (limit = 100, offset = 0) => 
    makeRequest('get', '/api/canopies', null, { limit, offset }),
  
  /**
   * Get canopies for a specific fleet
   * @param {string} fleetId - Fleet ID
   * @param {number} limit - Number of results to return
   * @param {number} offset - Offset for pagination
   * @returns {Promise} - Promise with fleet's canopies data
   */
  getCanopiesByFleetId: (fleetId, limit = 100, offset = 0) => 
    makeRequest('get', `/api/fleets/${fleetId}/canopies`, null, { limit, offset }),
  
  /**
   * Get canopy by ID
   * @param {string} canopyId - Canopy ID
   * @returns {Promise} - Promise with canopy data
   */
  getCanopyById: (canopyId) => 
    makeRequest('get', `/api/canopies/${canopyId}`),
    
  /**
   * Create a new canopy
   * @param {Object} canopyData - Canopy data object
   * @returns {Promise} - Promise with created canopy data
   */
  createCanopy: (canopyData) => 
    makeRequest('post', '/api/canopies', canopyData),
    
  /**
   * Update an existing canopy
   * @param {string} canopyId - Canopy ID
   * @param {Object} canopyData - Updated canopy data
   * @returns {Promise} - Promise with updated canopy data
   */
  updateCanopy: (canopyId, canopyData) => 
    makeRequest('put', `/api/canopies/${canopyId}`, canopyData),
    
  /**
   * Delete a canopy
   * @param {string} canopyId - Canopy ID to delete
   * @returns {Promise} - Promise with deletion result
   */
  deleteCanopy: (canopyId) => 
    makeRequest('delete', `/api/canopies/${canopyId}`),

  // Real-time subscription methods
  /**
   * Subscribe to canopy updates for a specific canopy
   * @param {string} canopyId - Canopy ID to subscribe to
   * @param {function} handler - Function to call when canopy is updated
   * @returns {function} - Unsubscribe function
   */
  subscribeToCanopyUpdates: (canopyId, handler) => {
    const wrappedHandler = (data, timestamp) => {
      // Transform snake_case data to camelCase for frontend
      const transformedData = snakeToCamel(data);
      if (transformedData.canopyId === canopyId) {
        handler(transformedData, timestamp);
      }
    };
    
    realtimeService.subscribe('canopy_updated', wrappedHandler);
    
    return () => {
      realtimeService.unsubscribe('canopy_updated', wrappedHandler);
    };
  },

  /**
   * Subscribe to canopy status changes for a specific canopy
   * @param {string} canopyId - Canopy ID to subscribe to
   * @param {function} handler - Function to call when canopy status changes
   * @returns {function} - Unsubscribe function
   */
  subscribeToCanopyStatusChanges: (canopyId, handler) => {
    const wrappedHandler = (data, timestamp) => {
      // Transform snake_case data to camelCase for frontend
      const transformedData = snakeToCamel(data);
      if (transformedData.canopyId === canopyId) {
        handler(transformedData, timestamp);
      }
    };
    
    realtimeService.subscribe('canopy_status_changed', wrappedHandler);
    
    return () => {
      realtimeService.unsubscribe('canopy_status_changed', wrappedHandler);
    };
  },

  /**
   * Subscribe to all canopy updates
   * @param {function} handler - Function to call when any canopy is updated
   * @returns {function} - Unsubscribe function
   */
  subscribeToAllCanopyUpdates: (handler) => {
    const wrappedHandler = (data, timestamp) => {
      // Transform snake_case data to camelCase for frontend
      const transformedData = snakeToCamel(data);
      handler(transformedData, timestamp);
    };
    
    realtimeService.subscribe('canopy_updated', wrappedHandler);
    
    return () => {
      realtimeService.unsubscribe('canopy_updated', wrappedHandler);
    };
  }
};

// App API Service
export const appService = {
  /**
   * Get all apps with pagination
   * @param {number} limit - Number of results to return
   * @param {number} offset - Offset for pagination
   * @returns {Promise} - Promise with apps data
   */
  getApps: (limit = 100, offset = 0) => 
    makeRequest('get', '/api/apps', null, { limit, offset }),
  
  /**
   * Get app by ID
   * @param {string} appId - App ID
   * @returns {Promise} - Promise with app data
   */
  getAppById: (appId) => 
    makeRequest('get', `/api/apps/${appId}`),
    
  /**
   * Get apps associated with a specific customer
   * @param {string} customerId - Customer ID
   * @param {number} limit - Number of results to return
   * @param {number} offset - Offset for pagination
   * @returns {Promise} - Promise with customer's apps data
   */
  getAppsByCustomerId: (customerId, limit = 100, offset = 0) => 
    makeRequest('get', `/api/customers/${customerId}/apps`, null, { limit, offset }),
    
  /**
   * Create a new app
   * @param {Object} appData - App data object
   * @returns {Promise} - Promise with created app data
   */
  createApp: (appData) => 
    makeRequest('post', '/api/apps', appData),
    
  /**
   * Update an existing app
   * @param {string} appId - App ID
   * @param {Object} appData - Updated app data
   * @returns {Promise} - Promise with updated app data
   */
  updateApp: (appId, appData) => 
    makeRequest('put', `/api/apps/${appId}`, appData),
    
  /**
   * Delete an app
   * @param {string} appId - App ID to delete
   * @returns {Promise} - Promise with deletion result
   */
  deleteApp: (appId) => 
    makeRequest('delete', `/api/apps/${appId}`)
};

// Telemetry API Service
export const telemetryService = {
  /**
   * Get telemetry data with filtering and pagination
   * @param {Object} params - Query parameters including deviceId, limit, offset
   * @returns {Promise} - Promise with telemetry data
   */
  getTelemetry: (params = {}) => {
    // Ensure limit and offset are always provided
    const queryParams = {
      limit: 20,
      offset: 0,
      ...params
    };
    return makeRequest('get', '/api/telemetry', null, queryParams);
  },
  
  /**
   * Get latest telemetry for a specific device
   * @param {string} deviceId - Device ID (same as canopy ID)
   * @returns {Promise} - Promise with latest telemetry data
   */
  getLatestTelemetry: (deviceId) => 
    makeRequest('get', `/api/telemetry/latest/${deviceId}`),

  // Real-time subscription methods
  /**
   * Subscribe to real-time telemetry updates for a specific canopy
   * @param {string} canopyId - Canopy ID to subscribe to
   * @param {function} handler - Function to call when telemetry is updated
   * @returns {function} - Unsubscribe function
   */
  subscribeToRealTime: (canopyId, handler) => {
    const wrappedHandler = (data, timestamp) => {
      // Transform snake_case data to camelCase for frontend
      const transformedData = snakeToCamel(data);
      // Note: database uses device_id field which maps to canopy_id
      if (transformedData.deviceId === canopyId) {
        handler(transformedData, timestamp);
      }
    };
    
    realtimeService.subscribe('telemetry_update', wrappedHandler);
    
    return () => {
      realtimeService.unsubscribe('telemetry_update', wrappedHandler);
    };
  },

  /**
   * Subscribe to all real-time telemetry updates
   * @param {function} handler - Function to call when any telemetry is updated
   * @returns {function} - Unsubscribe function
   */
  subscribeToAllTelemetry: (handler) => {
    const wrappedHandler = (data, timestamp) => {
      // Transform snake_case data to camelCase for frontend
      const transformedData = snakeToCamel(data);
      handler(transformedData, timestamp);
    };
    
    realtimeService.subscribe('telemetry_update', wrappedHandler);
    
    return () => {
      realtimeService.unsubscribe('telemetry_update', wrappedHandler);
    };
  }
};

const apiClient = {
  meService,
  adminUsersService,
  adminDevicesService,

  customerService,
  fleetService,
  canopyService,
  appService,
  telemetryService
};

export default apiClient;
